/**
 * Component specifically designed for displaying a status like indicators.
 * For most usages you must use the design system's Icon component instead.
 *
 * This component is used for:
 * - Status indicators: Flow, Agents, Jobs, etc.
 * - Color pallete of Charts: Analytics, Simulations, etc.
 */
import { forwardRef } from "react";

import { faDash } from "@fortawesome/pro-regular-svg-icons";
import { faCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { twJoin } from "tailwind-merge";

import {
  COLOR_HEX,
  type ColorIconColor,
} from "src/design-system/utils/colorPalette";
import { getSize } from "src/design-system/utils/sizes";

type ColorIconSize = "3xs" | "2xs" | "xs";

type ColorIconIcon = "circle" | "dash";

type ColorIconProps = {
  color: ColorIconColor;
  icon?: ColorIconIcon;
  size?: ColorIconSize;
  dataLoc?: string;
};

const HEX_TO_COLOR = Object.fromEntries(
  Object.entries(COLOR_HEX).map(([name, hex]) => [hex, name]),
) as Record<string, ColorIconColor>;

export const getColorByHex = (hex: string): ColorIconColor => HEX_TO_COLOR[hex];

const ICONS = {
  circle: faCircle,
  dash: faDash,
} as const;

export const ColorIcon = forwardRef<HTMLSpanElement, ColorIconProps>(
  ({ color, icon = "circle", size = "3xs", dataLoc }, ref) => {
    const { width, height } = getSize(size);

    return (
      <span ref={ref} className="flex justify-center p-1">
        <FontAwesomeIcon
          className={twJoin(
            "aspect-square transition-all",
            "cursor-[inherit]",
            width,
            height,
          )}
          data-loc={dataLoc}
          icon={ICONS[icon]}
          style={{ color: COLOR_HEX[color] }}
        />
      </span>
    );
  },
);
